import React from 'react';
import './PricingAndPackaging.scss';
import { dangerousGettext, gettext } from '@eventbrite/i18n';
import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import styles from './PricingComparisonCards.module.scss';
import {
    getHeapDataAttribute,
    HeapAttributes,
} from '@eventbrite/marketing-tools-common';
import {
    BoltFill,
    Tickets,
    ArrowRightChunky,
} from '@eventbrite/eds-iconography';
import { Badge, CheckChunkierIcon, Typography } from '@eventbrite/marmalade';
import { getHost, getLocale } from './utils';
import {
    getPricesForTLD,
    getFlexDiscountedPricesForTLD,
} from './pricingAndPackagingUtils';
import { AnnualDiscountBadge } from '@eventbrite/pricing-and-packaging-ui';
import classNames from 'classnames';

const { Teams, Features, Components, Actions } = HeapAttributes;

const newFlexDiscountedPrice = getFlexDiscountedPricesForTLD();

const TicketCostPerEvent = (props) => {
    const perEventPrice = props.discountedPrice ? (
        <>
            <span className="eds-text--strikethrough eds-text-color--ui-600">
                {props.planCost}
            </span>
            &nbsp;
            <span className="eds-text-bs eds-text-weight--heavy eds-text-color--ui-700">
                {props.discountedPrice}
            </span>
        </>
    ) : (
        <span>{props.planCost}</span>
    );

    return (
        <span>
            {perEventPrice}
            &nbsp;
            {props.eventSlabText}
        </span>
    );
};

const getFlexPlanDetailsWithDiscountedPrice = (priceMapping) => {
    const coreTier = (
        <TicketCostPerEvent
            planCost={priceMapping.flexPlan.coreTier}
            discountedPrice={newFlexDiscountedPrice?.coreTier}
            eventSlabText={gettext('per event for up to 250 tickets')}
        />
    );

    const proTier = (
        <TicketCostPerEvent
            planCost={priceMapping.flexPlan.proTier}
            discountedPrice={newFlexDiscountedPrice?.proTier}
            eventSlabText={gettext('per event for unlimited tickets')}
        />
    );

    return [coreTier, proTier];
};

const getFlexPlanOffer = () => {
    return newFlexDiscountedPrice?.coreTier ||
        newFlexDiscountedPrice?.proTier ? (
        <Badge
            variant="custom"
            bordered={false}
            style={{ backgroundColor: '#C3F7E0' }}
        >
            <Typography as="p" color="neutral-900" variant="body-sm-bold">
                {gettext('Limited-time discount').toString()}
            </Typography>
        </Badge>
    ) : null;
};

const getCardDetails = () => {
    const priceMapping = getPricesForTLD();
    return [
        {
            topBanner: null,
            planName: gettext('Free'),
            subtext: gettext('Host your event for free*'),
            cost: (
                <div>
                    <span className={styles.cost}>
                        {priceMapping.freePlan.freeTier}
                    </span>
                    <span className={styles.frequency}>
                        {gettext(' per event')}
                    </span>
                </div>
            ),
            tickets: gettext('For up to 25 tickets'),
            planDetails: [],
            offer: null,
            perks: [
                gettext(
                    'Host an unlimited number of events with up to 25 tickets',
                ),
                gettext('Send up to 250 marketing emails a day'),
                gettext(
                    'Access all-in-one event ticketing and marketing tools',
                ),
            ],
            cta: {
                link: '/signin/signup/?referrer=%2Fmanage%2Fevents%2Fcreate%2F',
                style: 'neutral',
                text: gettext('Get started for free'),
                heapId: getHeapDataAttribute({
                    team: Teams.Marketing,
                    feature: Features.PricingPage,
                    component: Components.FreePlanCardCTA,
                    action: Actions.Click,
                }),
            },
        },
        {
            topBanner: (
                <div className={styles.payPerEventBanner}>
                    {gettext('Pay per event')}
                </div>
            ),
            planName: gettext('Flex'),
            subtext: gettext('Pay as you go for full flexibility*'),
            cost: (
                <div>
                    <span className={styles.cost}>
                        {priceMapping.flexPlan.liteTier}
                    </span>
                    <span className={styles.frequency}>
                        {gettext(' per event')}
                    </span>
                </div>
            ),
            tickets: gettext('For up to 100 tickets'),
            planDetails: getFlexPlanDetailsWithDiscountedPrice(priceMapping),
            offer: getFlexPlanOffer(),
            perks: [
                gettext('Pay from your ticket sales for no upfront costs'),
                gettext('Host larger events and pay as you go'),
            ],
            cta: {
                link: '/signin/signup/?referrer=%2Fmanage%2Fevents%2Fcreate%2F',
                style: 'neutral',
                text: gettext('Create your first event'),
                heapId: getHeapDataAttribute({
                    team: Teams.Marketing,
                    feature: Features.PricingPage,
                    component: Components.FlexPlanCardCTA,
                    action: Actions.Click,
                }),
            },
        },
        {
            topBanner: (
                <div className={styles.subscribeAndSaveBanner}>
                    <span>{gettext('Subscribe and save')}</span>
                    <Icon type={<BoltFill />} color="white" size="xsmall" />
                </div>
            ),
            planName: gettext('Pro'),
            subtext: gettext('Save when hosting frequent events*'),
            cost: (
                <div>
                    <span className={styles.cost}>
                        {priceMapping.proPlan.liteTier}
                    </span>
                    <span className={styles.frequency}>
                        {gettext(' per month')}
                    </span>
                </div>
            ),
            tickets: gettext('For up to 100 tickets'),
            planDetails: [
                gettext('%(cost)s per month for up to 250 tickets', {
                    cost: priceMapping.proPlan.coreTier,
                }),
                gettext('%(cost)s per month for unlimited tickets', {
                    cost: priceMapping.proPlan.proTier,
                }),
            ],
            offer: (
                <>
                    <div>
                        <AnnualDiscountBadge />
                    </div>
                    <div>
                        <Typography
                            as="p"
                            color="neutral-900"
                            variant="body-sm"
                        >
                            {gettext('when billed yearly').toString()}
                        </Typography>
                    </div>
                </>
            ),
            perks: [
                gettext('Host unlimited events with your monthly subscription'),
                gettext('Send up to 10,000 marketing emails a day'),
            ],
            cta: {
                link: '/signin/signup/?referrer=%2Fmanage%2Fevents%2Fcreate%2F',
                style: 'fill',
                text: gettext('Try free for 14 days'),
                heapId: getHeapDataAttribute({
                    team: Teams.Marketing,
                    feature: Features.PricingPage,
                    component: Components.ProPlanCardCTA,
                    action: Actions.Click,
                }),
            },
        },
        {
            topBanner: null,
            planName: gettext('Premium'),
            subtext: gettext('Plans for large or complex paid events*'),
            cost: (
                <div>
                    <span className={styles.cost}>{gettext('Custom')}</span>
                </div>
            ),
            tickets: gettext('Unlimited tickets'),
            planDetails: [],
            offer: null,
            perks: [
                gettext('No fees to publish'),
                gettext('Custom Ticketing Fees'),
                gettext('1:1 Custom onboarding'),
                gettext('24/7 Chat support'),
                gettext('Phone support'),
            ],
            cta: {
                link: '/l/contact-eventbrite-sales/',
                style: 'neutral',
                text: gettext('Contact Sales'),
                heapId: getHeapDataAttribute({
                    team: Teams.Marketing,
                    feature: Features.PricingPage,
                    component: Components.PremiumPlanCardCTA,
                    action: Actions.Click,
                }),
            },
        },
    ];
};

export const PricingAndPackagingComparisonCards = () => {
    const cardDetails = getCardDetails();
    const locale = Intl.NumberFormat().resolvedOptions().locale;

    return (
        <div className={styles.comparisonCardsContainer}>
            <div className={styles.cards}>
                {cardDetails.map((card, i) => {
                    const hasTopBanner = !!card.topBanner;
                    const previousCard = cardDetails[i - 1];
                    const hasOffer = !!card.offer;
                    return (
                        <div
                            key={i}
                            className={classNames(styles.card, {
                                [styles.cardWithoutBanner]: !hasTopBanner,
                            })}
                        >
                            {hasTopBanner && (
                                <div className={styles.topBanner}>
                                    {card.topBanner}
                                </div>
                            )}
                            <div className={styles.content}>
                                <div className={styles.plan}>
                                    <h5 className={styles.name}>
                                        {card.planName}
                                    </h5>
                                    <p className={styles.subtext}>
                                        {card.subtext}
                                    </p>
                                    <h3 className={styles.cost}>{card.cost}</h3>
                                    <p className={styles.tickets}>
                                        {card.tickets}
                                    </p>
                                    <div className={styles.details}>
                                        {card.planDetails.map((detail, i) => {
                                            return <p key={i}>{detail}</p>;
                                        })}
                                    </div>
                                    {hasOffer && (
                                        <div className={styles.offer}>
                                            {card.offer}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.perks}>
                                    {!!previousCard && (
                                        <p
                                            className={
                                                styles.allPreviousPerksText
                                            }
                                        >
                                            {gettext(
                                                'Everything in %(previousPlanName)s, plus:',
                                                {
                                                    previousPlanName:
                                                        previousCard.planName,
                                                },
                                            )}
                                        </p>
                                    )}
                                    {card.perks.map((perk, i) => {
                                        return (
                                            <div
                                                className={styles.perk}
                                                key={i}
                                            >
                                                <Icon
                                                    type={<CheckChunkierIcon />}
                                                    size="xsmall"
                                                />
                                                <p>{perk}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className={styles.cta}>
                                    <Button
                                        style={card.cta.style}
                                        onClick={() => {
                                            window.location.href =
                                                card.cta.link;
                                        }}
                                        data-heap-id={card.cta.heapId}
                                    >
                                        {card.cta.text}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* <div
                className={styles.comparePlansLink}
                onClick={() =>
                    (window.location.href = '/organizer/compare-packages/')
                }
            >
                <span>{gettext('Compare plans')}</span>
                <Icon
                    type={<ArrowRightChunky />}
                    color="ui-blue--hover"
                    size="xsmall"
                />
            </div> */}
            <p
                className={styles.disclaimer}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: dangerousGettext(
                        '*For all plans, ad spend and <a href="%(ticketingFeesUrl)s" target="__blank">Ticketing Fees</a> may apply. Ticketing Fees are paid by ticket buyers unless you choose to cover them.',
                        {
                            ticketingFeesUrl: `https://${getHost()}/help/${locale.toLowerCase()}/articles/755615`,
                        },
                    ),
                }}
            />
        </div>
    );
};

export default PricingAndPackagingComparisonCards;
